<template>
    <!--
        Where a paused queue is found again.

        The footer is the only surface that shows a running queue, so pausing it
        would otherwise put a half-worked day somewhere with no door back in. This
        card is that door — and it is shown for a running queue too, because
        "do I have a queue on, and how far through am I" is a question worth
        answering in the place an agent already goes to see their day.
    -->
    <div
        v-if="hasActiveQueue"
        class="card flex flex-col gap-2 rounded-lg p-3"
        data-test="queue-activity-card"
    >
        <div class="flex items-center justify-between gap-x-2">
            <div class="flex items-center gap-x-2">
                <SvgIcon
                    :name="isPaused ? 'system-pause' : 'system-play'"
                    class="text-muval-gray-1 size-4 shrink-0"
                />
                <span class="text-sm font-medium">Working queue</span>
                <span
                    class="rounded-muval-1 text-xxs h-5 px-2"
                    :class="isPaused ? 'bg-muval-amber/30 text-muval-amberr' : 'bg-muval-sky-2 text-muval-dusk-2'"
                    data-test="queue-activity-state"
                >
                    {{ isPaused ? 'Paused' : 'In progress' }}
                </span>
            </div>
            <span
                class="text-muval-gray-2 shrink-0 text-sm tabular-nums"
                data-test="queue-activity-position"
            >
                {{ progress.position || '--' }} of {{ progress.total }}
            </span>
        </div>

        <!-- The same segments the footer draws, so the two never disagree about progress. -->
        <div class="bg-muval-gray-6 flex h-1.5 w-full gap-px overflow-hidden rounded-full">
            <div
                v-for="item in items"
                :key="item.id"
                class="h-full flex-1"
                :class="segmentClass(item)"
                :data-state="segmentState(item)"
            />
        </div>

        <div
            v-if="currentAction"
            class="text-muval-gray-1 flex items-center gap-x-2 text-xs"
            data-test="queue-activity-current"
        >
            <SvgIcon
                :name="actionTypeIcon(currentAction.action_type)"
                class="size-3.5 shrink-0"
            />
            <span class="truncate">
                {{ actionTypeLabel(currentAction.action_type) }} ·
                {{ currentAction.order?.customer_name ?? '--' }}
            </span>
        </div>

        <div class="flex items-center justify-between gap-x-2">
            <span class="text-muval-gray-2 text-xs">
                {{ progress.done }} done · {{ progress.skipped }} skipped · {{ progress.pending }} outstanding
            </span>
            <button
                type="button"
                class="bg-brand shrink-0 rounded-full px-3 py-1 text-sm text-white transition disabled:opacity-40"
                :disabled="isWriting"
                :data-test="isPaused ? 'queue-activity-resume' : 'queue-activity-goto'"
                @click="isPaused ? resume() : goToCurrent()"
            >
                {{ isPaused ? 'Resume' : 'Go to current' }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { actionTypeIcon, actionTypeLabel } from '@util/actionTypeMeta';
import { useSalesQueue, type SalesQueueItem } from '@composables/useSalesQueue';

const emit = defineEmits<{ (e: 'close'): void }>();

const router = useRouter();

const { queue, items, currentItem, currentAction, hasActiveQueue, isPaused, progress, isWriting, setPaused } =
    useSalesQueue();

const segmentState = (item: SalesQueueItem) =>
    item.done ? 'done'
    : item.skipped ? 'skipped'
    : 'pending';

const segmentClass = (item: SalesQueueItem) => {
    if (item.id === queue.value?.current_item_id) {
        return 'bg-brand';
    }

    return { done: 'bg-muval-lime-1', skipped: 'bg-muval-amber', pending: 'bg-muval-gray-4' }[segmentState(item)];
};

/**
 * Resuming only un-pauses. The footer owns the navigation that follows, so the
 * page moves for exactly the same reason it moves on any other cursor change and
 * there is no second copy of that rule here.
 */
async function resume(): Promise<void> {
    if (!queue.value) {
        return;
    }

    try {
        await setPaused.mutateAsync({ queueId: queue.value.id, paused: false });
        emit('close');
    } catch {
        // Reported by the composable; the queue is untouched and the panel stays open.
    }
}

/** A running queue needs no state change — just the page it is pointing at. */
function goToCurrent(): void {
    const orderId = currentItem.value?.action?.order?.id;

    if (orderId) {
        router.push({ name: 'OrderView', params: { id: orderId } });
    }

    emit('close');
}
</script>
